import { ProductCategory } from '../products/categories';
import products from '../products/products';

export function randomId(): string {
    return Math.round(Math.random() * 1e18)
        .toString(36)
        .substring(0, 10);
}

export function isWindsProduct(id: number): boolean {
    return id >= 0x4080 && id <= 0x4198;
}

export function isCAECompactWindsProduct(id: number): boolean {
    return id >= 0x4160 && id <= 0x4170;
}

export function isSFEWindsProduct(id: number): boolean {
    return id >= 0x4160 && id <= 0x418f;
}

export function isMETWindsProduct(id: number): boolean {
    return id >= 0x4080 && id <= 0x4087;
}

export function isTracksProduct(id: number): boolean {
    return id >= 0x6c && id <= 0x6f;
}

export function isRaimProduct(id: number): boolean {
    return id >= 0x73 && id <= 0x78;
}

export function isTurbulenceHotpoly(id: number): boolean {
    return (
        (id >= 0x8120 && id <= 0x812f) ||
        (id >= 0x8117 && id <= 0x811f) ||
        (id >= 0x8150 && id <= 0x815f) ||
        (id >= 0x8550 && id <= 0x856b) ||
        (id >= 0x8180 && id <= 0x818f)
    );
}

export function isIcingHotpoly(id: number): boolean {
    const icingHotpolyIds = [0x851b, 0x851c, 0x8530, 0x8519, 0x851a, 0x8517, 0x8518, 0x600f];
    return (
        icingHotpolyIds.includes(id) ||
        (id >= 0x8160 && id <= 0x816f) ||
        (id >= 0x8140 && id <= 0x814f) ||
        (id >= 0x8511 && id <= 0x8516) ||
        (id >= 0x8540 && id <= 0x854f) ||
        (id >= 0x8530 && id <= 0x8539) ||
        (id >= 0x853a && id <= 0x853f) ||
        (id >= 0x8520 && id <= 0x852f) ||
        (id >= 0x85f0 && id <= 0x85fa)
    );
}
export function isCustomPolygonLayer(id: number): boolean {
    // EDR&Icing
    return (
        (id >= 0x8140 && id <= 0x815f) ||
        (id >= 0x8160 && id <= 0x816f) ||
        (id >= 0x8110 && id <= 0x811f) || // EDR
        (id >= 0x8150 && id <= 0x815f) || // EDR Fcst
        (id >= 0x8120 && id <= 0x812f) || // turb cat
        (id >= 0x8180 && id <= 0x818f) || // gtg nowcast
        (id >= 0x8550 && id <= 0x8576) || // enhanced EDR
        (id >= 0x8511 && id <= 0x854f) ||
        (id >= 0x85f1 && id <= 0x85fa) ||
        id === 0x8170 || // Cumulonimbus
        id === 0x8171 || // Cumulonimbus
        id === 0x85d2 || // Enhanced thunderstormsŪ
        id === 0x85d3 || // Enhanced thunderstorms
        id === 0x1021 || // radar rainfall estimate
        id === 0x1022 || // radar composite contoured
        (id >= 0x6090 && id <= 0x60a1)
    );
}

export function isLightningCustom(id: number): boolean {
    return id === 0x5010 || id === 0x5020 || id === 0x5080 || id === 0x5081 || id === 0x5082 || id === 0x5000;
}

const sigProducts = new Set<number>([
    0x8100, 0x8101, 0x8102, 0x8103, 0x8172, 0x8104, 0x8105, 0x8106, 0x8107, 0x8173, 0x8108, 0x8109, 0x810a, 0x810b, 0x8174, 0x810c, 0x810d,
    0x810e, 0x810f, 0x8175, 0x8608, 0x8609, 0x860a, 0x860b, 0x8600, 0x8601, 0x8602, 0x8603, 0x860c, 0x860d, 0x860e, 0x860f, 0x8604, 0x8605,
    0x8606, 0x8607, 0x8610, 0x8611, 0x8612, 0x8613, 0x8614, 0x8615, 0x8616, 0x8617, 0x8403, 0x8404, 0x8405, 0x8406, 0x8407, 0x8408, 0x8409,
    0x840a, 0x8176, 0x8177
]);

export function isSigWx(id: number) {
    return sigProducts.has(id);
}

const frontLayers = new Set<number>([0x0004, 0x0005, 0x0006, 0x6024, 0x85e0, 0x8401, 0x8402]);

export function isFront(id: number) {
    return frontLayers.has(id);
}

export function getSeverityString(productCode: number, severity: number): string {
    const product = products[productCode];
    if (!product) {
        return '';
    }

    if (product.category == ProductCategory.turbulence) {
        //handle 2 different types of severity for Turbulence
        if (productCode >= 0x8120 && productCode <= 0x8125) {
            switch (severity) {
                case 0:
                    return 'None';
                case 1:
                    return 'Light';
                case 2:
                    return 'Moderate';
                case 3:
                    return 'Severe';
                case 4:
                    return 'Extreme';
                default:
                    return '';
            }
        }
        let bSeverity = 0;
        if (severity > 0) {
            bSeverity = severity / 10 + 1;
        }

        switch (bSeverity) {
            case 0:
                return 'None';
            case 1:
                return '>0.0 to <0.1';
            case 2:
                return '0.1 to <0.2';
            case 3:
                return '0.2 to <0.3';
            case 4:
                return '0.3 to <0.4';
            case 5:
                return '0.4 to <0.5';
            case 6:
                return '0.5 to <0.6';
            case 7:
                return '0.6 to <0.7';
            case 8:
                return '0.7 to <0.8';
            case 9:
                return '0.8 to <0.9';
            case 10:
            case 11:
                return '0.9 to 1.0'; // Handle severity == 1.0
            default:
                return '';
        }
    }

    switch (product.category) {
        case ProductCategory.icing:
            switch (severity) {
                case 0:
                    return 'None';
                case 1:
                    return 'Trace';
                case 2:
                    return 'Light';
                case 3:
                    return 'Moderate';
                case 4:
                    return 'Heavy';
                default:
                    return '';
            }
        case ProductCategory.airspace:
            switch (severity) {
                case 0:
                    return 'None';
                case 1:
                    return 'Negative'; // Not currently used
                case 2:
                    return 'Trace'; // Not currently used
                case 3:
                    return 'Trace-Light'; // Not currently used
                case 4:
                    return 'Light'; // Not currently used
                case 5:
                    return 'Light-Mod'; // Not currently used
                case 6:
                    return 'Moderate';
                case 7:
                    return 'Mod-Sev';
                case 8:
                    return 'Severe';
                default:
                    return '';
            }
        default:
            return '';
    }
}
